import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { IAppHeaderProps, IOptimoLogoProps } from './AppHeader.types'
import * as S from './AppHeader.styles'

import { ProfileMenu } from '@/components/common/ProfileMenu'
import { FeedbackButton } from '@/components/common/FeedbackButton'
import { ImpersonationBanner } from '@/components/common/ImpersonationBanner'
import { NotificationAppHeader } from '@/features/notifications'
import { selectUserViewType } from '@/store/selectors/authSelectors'
import { useCurrentUser } from '@/hooks/users/useCurrentUser'
import { useUserProfile } from '@/hooks/users/useUserProfile'
import { useLogout } from '@/hooks/auth/useLogout'
import { useUI } from '@/contexts/UIContext'
import { useResponsive } from '@/hooks/useResponsive'
import { clearAllTokens } from '@/utils/auth/tokenManager'
import { clearAuth } from '@/store/slices/authSlice'
import { asyncToast } from '@/components/ui/Toast/asyncToast'
import { NavigationRoutes } from '@/router/navigation.types'
import type { RootState } from '@/store'
import type { UserType } from '@/features/notifications'
import optimoLogo from '@/assets/images/optimo/light-backgrounds/optimo-logo-full-medium.png'
import { useImpersonationStatus } from '@/hooks/impersonation/useImpersonationStatus'

export const OptimoLogo = ({}: IOptimoLogoProps) => {
    const { isMobile } = useResponsive()

    return (
        <S.LogoContainer>
            <S.LogoImage src={optimoLogo} alt="Optimo" $isMobile={isMobile} />
            {!isMobile && (
                <S.LogoText>
                    <S.LogoSubtitle>People Intelligence Platform</S.LogoSubtitle>
                </S.LogoText>
            )}
        </S.LogoContainer>
    )
}

export const AppHeader = ({ showMobileNav = false, mobileNavTrigger }: IAppHeaderProps) => {
    const currentView = useSelector(selectUserViewType)
    const isMockAuth = useSelector((state: RootState) => state.auth.isMockAuth)
    const reduxUser = useSelector((state: RootState) => state.auth.user)

    // Use Redux user for mock auth, API user for real auth
    const { data: apiUser } = useCurrentUser({ enabled: !isMockAuth })
    const currentUser = isMockAuth ? reduxUser : apiUser

    const { data: profileData } = useUserProfile({
        userUuid: currentUser?.uuid || '',
        enabled: !!currentUser?.uuid && !isMockAuth,
    })
    const { setShowFeedbackModal } = useUI()
    const { isMobile } = useResponsive()
    const logoutMutation = useLogout()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const { status } = useImpersonationStatus()

    if (!currentView || !currentUser) return null

    // Map the currentView to UserType for notifications
    const getUserType = (view: string): UserType => {
        switch (view.toLowerCase()) {
            case 'manager':
            case 'people_manager':
                return 'manager'
            case 'hr':
            case 'hr_admin':
                return 'hr'
            case 'hr_manager':
                return 'employee'
            case 'employee':
            default:
                return 'employee'
        }
    }

    const notificationUserType = getUserType(currentView)

    const handleLogoutClick = () => {
        if (isMockAuth) {
            // For mock auth, handle logout locally without API call
            clearAllTokens()
            dispatch(clearAuth())
            queryClient.clear()
            toast.success('Successfully logged out')
            navigate(NavigationRoutes.LOGIN, { replace: true })
        } else {
            // For real auth, use the logout hook with asyncToast
            asyncToast({
                id: 'logout',
                promise: logoutMutation.mutateAsync(),
                msgs: {
                    loading: 'Logging out...',
                    success: 'Successfully logged out',
                    error: 'Failed to logout',
                },
            })
        }
    }

    // Create user object for ProfileMenu
    const user = {
        id: '1',
        fullName: ('display_name' in currentUser ? currentUser.display_name : currentUser.full_name) || currentUser.full_name,
        email: currentUser.email,
        role: currentView,
        avatarUrl: profileData?.profile_picture_url || undefined,
        avatarType: 'default' as const,
    }

    return (
        <>
            <S.HeaderContainer $isMobile={isMobile}>
                {/* Subtle gradient background */}
                <S.GradientBackground />

                <S.HeaderContent $isMobile={isMobile}>
                    <S.HeaderRow>
                        {/* Left side - Mobile nav + Logo */}
                        <S.LeftSection>
                            {/* Mobile Navigation Trigger */}
                            {showMobileNav && mobileNavTrigger}

                            {/* Logo Section */}
                            <OptimoLogo />
                        </S.LeftSection>

                        {/* Right side - User actions */}
                        <S.RightSection>
                            {/* User Greeting - Desktop only, hidden during impersonation */}
                            {!status?.is_impersonating && (
                                <S.UserGreeting>
                                    <S.GreetingText>
                                        Welcome back, {('display_name' in currentUser ? currentUser.display_name : currentUser.full_name.split(' ')[0]) || currentUser.full_name.split(' ')[0]}!
                                    </S.GreetingText>
                                </S.UserGreeting>
                            )}

                            {/* Impersonation Banner - same component used in layout */}
                            {status?.is_impersonating && <ImpersonationBanner />}

                            {/* Feedback Button - Responsive */}
                            <FeedbackButton onClick={() => setShowFeedbackModal(true)} isMobile={isMobile} />

                            {/* Enhanced Notifications */}
                            <NotificationAppHeader initialUserType={notificationUserType} />

                            {/* Profile Menu */}
                            <ProfileMenu user={user} onLogout={handleLogoutClick} />
                        </S.RightSection>
                    </S.HeaderRow>
                </S.HeaderContent>
            </S.HeaderContainer>
        </>
    )
}
